/**
 * Confronto tra l'ordine ORIGINALE inviato (snapshot alla prima modifica del magazzino)
 * e lo stato attuale delle righe: quantità cambiate, articoli mancanti, aggiunti, prezzi variati.
 */
import React, { useState } from "react";
import { ChevronDown, GitCompareArrows, ArrowRight } from "lucide-react";
import { Badge } from "./ui/badge";

const nf = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 }));

const buildDiff = (order) => {
  const orig = order.original_items || [];
  const curr = order.items || [];
  if (!orig.length) return null;
  const usedCurr = new Set();
  const rows = [];

  orig.forEach((o) => {
    let mi = -1;
    for (let i = 0; i < curr.length; i++) {
      if (usedCurr.has(i)) continue;
      const c = curr[i];
      if ((o.product_id && c.product_id === o.product_id) || ((o.code || "") !== "" && (c.code || "") === (o.code || ""))) { mi = i; break; }
    }
    if (mi === -1) {
      rows.push({ type: "removed", code: o.code, description: o.description, ordQty: o.quantity, ordUnit: o.unit, ordPrice: o.unit_price });
      return;
    }
    usedCurr.add(mi);
    const c = curr[mi];
    const missing = c.availability_status === "missing";
    const shipQty = missing ? 0 : (c.actual_quantity ?? c.quantity);
    const qtyDiff = c.unit !== o.unit || Math.abs((shipQty || 0) - (o.quantity || 0)) > 0.001;
    const priceDiff = Math.abs((c.unit_price || 0) - (o.unit_price || 0)) > 0.001;
    rows.push({
      type: missing ? "missing" : (qtyDiff || priceDiff ? "changed" : "ok"),
      code: o.code, description: o.description,
      ordQty: o.quantity, ordUnit: o.unit, ordPrice: o.unit_price,
      shipQty, shipUnit: c.unit, shipPrice: c.unit_price,
      qtyDiff, priceDiff,
      note: c.warehouse_note,
    });
  });

  curr.forEach((c, i) => {
    if (usedCurr.has(i)) return;
    rows.push({ type: "added", code: c.code, description: c.description, shipQty: c.actual_quantity ?? c.quantity, shipUnit: c.unit, shipPrice: c.unit_price });
  });

  const diffCount = rows.filter((r) => r.type !== "ok").length;
  return { rows, diffCount };
};

const ROW_STYLE = {
  ok: "bg-white",
  changed: "bg-amber-50",
  missing: "bg-red-50",
  removed: "bg-red-50",
  added: "bg-cyan-50",
};
const ROW_BADGE = {
  ok: null,
  changed: { label: "Modificato", cls: "bg-amber-100 text-amber-800" },
  missing: { label: "Non inviato", cls: "bg-red-100 text-red-700" },
  removed: { label: "Rimosso", cls: "bg-red-100 text-red-700" },
  added: { label: "Aggiunto dal magazzino", cls: "bg-cyan-100 text-cyan-800" },
};

export const OrderOriginalCompare = ({ order, fm }) => {
  const [open, setOpen] = useState(false);
  const diff = buildDiff(order);
  if (!diff) return null;

  const snap = order.original_snapshot || {};
  const currentGrand = (order.final_grand_total ?? order.grand_total ?? order.total_amount) || 0;
  const origGrand = snap.grand_total || 0;
  const totDelta = currentGrand - origGrand;

  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="order-original-compare">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-stone-50 hover:bg-stone-100 text-left"
        data-testid="order-original-compare-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-stone-800">
          <GitCompareArrows className="w-4 h-4 text-[#8A1C2A]" />
          Confronto con ordine originale
          {diff.diffCount > 0 ? (
            <Badge className="bg-amber-100 text-amber-800" data-testid="compare-diff-count">{diff.diffCount} differenz{diff.diffCount === 1 ? "a" : "e"}</Badge>
          ) : (
            <Badge className="bg-emerald-100 text-emerald-700" data-testid="compare-no-diff">Nessuna differenza</Badge>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="divide-y divide-stone-100">
          {snap.at && (
            <p className="px-3 py-1.5 text-[11px] text-stone-400 bg-white">
              Ordine originale fotografato il {new Date(snap.at).toLocaleString("it-IT")} (prima modifica del magazzino)
            </p>
          )}
          {diff.rows.map((r, i) => (
            <div key={i} className={`px-3 py-2 ${ROW_STYLE[r.type]}`} data-testid={`compare-row-${r.code || i}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-[11px] text-stone-500 mr-1.5">{r.code}</span>
                  <span className="text-sm font-medium">{r.description}</span>
                  {r.note && <span className="block text-[11px] italic text-stone-500">📝 {r.note}</span>}
                </div>
                {ROW_BADGE[r.type] && <Badge className={`${ROW_BADGE[r.type].cls} text-[10px] shrink-0`}>{ROW_BADGE[r.type].label}</Badge>}
              </div>
              <div className="flex items-center gap-4 flex-wrap mt-1 text-xs">
                {r.type === "added" ? (
                  <span className="text-cyan-800">Spedito: <strong>{nf(r.shipQty)} {r.shipUnit}</strong>{r.shipPrice != null && <> × {fm ? fm(r.shipPrice) : `€ ${nf(r.shipPrice)}`}</>}</span>
                ) : (
                  <>
                    <span className={`inline-flex items-center gap-1 ${r.qtyDiff || r.type === "missing" || r.type === "removed" ? "font-semibold" : "text-stone-600"}`}>
                      Q.tà: {nf(r.ordQty)} {r.ordUnit}
                      {(r.type === "changed" || r.type === "missing") && r.qtyDiff !== false && (
                        <>
                          <ArrowRight className="w-3 h-3 text-stone-400" />
                          <span className={r.type === "missing" ? "text-red-700" : "text-amber-800"}>{nf(r.shipQty)} {r.shipUnit || r.ordUnit}</span>
                        </>
                      )}
                      {r.type === "removed" && <span className="text-red-700 ml-1">→ non presente</span>}
                    </span>
                    {r.priceDiff && (
                      <span className="inline-flex items-center gap-1 font-semibold">
                        Prezzo: {fm ? fm(r.ordPrice) : `€ ${nf(r.ordPrice)}`}
                        <ArrowRight className="w-3 h-3 text-stone-400" />
                        <span className="text-amber-800">{fm ? fm(r.shipPrice) : `€ ${nf(r.shipPrice)}`}</span>
                      </span>
                    )}
                    {r.type === "ok" && <span className="text-emerald-600">✓ conforme</span>}
                  </>
                )}
              </div>
            </div>
          ))}
          <div className="px-3 py-2 bg-stone-50 flex items-center justify-between text-sm">
            <span className="text-stone-600">Totale: {fm ? fm(origGrand) : `€ ${nf(origGrand)}`} → <strong>{fm ? fm(currentGrand) : `€ ${nf(currentGrand)}`}</strong></span>
            {Math.abs(totDelta) > 0.001 && (
              <span className={`font-semibold ${totDelta > 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="compare-total-delta">
                {totDelta > 0 ? "+" : ""}{fm ? fm(totDelta) : `€ ${nf(totDelta)}`}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderOriginalCompare;
